import React from 'react';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  GlobalCostCenter,
  CostCenterType,
  MockProject,
  MockWorkCategory
} from './PurchaseOrderFormData';
import { Building2, Briefcase } from 'lucide-react';

interface GlobalCostCenterPanelProps {
  costCenter: GlobalCostCenter | null;
  onChange: (costCenter: GlobalCostCenter) => void;
  projects: MockProject[];
  generalWorkCategories: MockWorkCategory[];
}

export const GlobalCostCenterPanel: React.FC<GlobalCostCenterPanelProps> = ({ costCenter, onChange, projects, generalWorkCategories }) => {
  const handleTypeChange = (costCenterType: CostCenterType) => {
    onChange({
      costCenterType,
      projectId: null,
      generalCostCenterId: null,
      workCategoryId: 0,
    });
  };

  const handleProjectChange = (projectId: number) => {
    onChange({
      ...costCenter!,
      projectId,
      workCategoryId: 0,
    });
  };

  const handleWorkCategoryChange = (workCategoryId: number) => {
    onChange({
      ...costCenter!,
      workCategoryId,
    });
  };

  const selectedProject = costCenter?.projectId
    ? projects.find(p => p.id === costCenter.projectId)
    : null;

  const availableWorkCategories = costCenter?.costCenterType === 'PROJECT'
    ? selectedProject?.workCategories || []
    : generalWorkCategories.filter(cat => !cat.isSystemDefault);

  return (
    <div className="overflow-hidden border rounded-lg shadow-sm bg-card border-border">
      <div className="px-4 py-5 border-b sm:px-6 border-border">
        <h3 className="flex items-center gap-2 text-lg font-semibold text-foreground">
          <Building2 className="w-5 h-5 text-primary" />
          Centro de Costo Global
        </h3>
        <p className="mt-2 text-sm text-muted-foreground">
          Todos los items se asignarán a este centro de costo
        </p>
      </div>

      <div className="px-4 py-6 sm:px-6 space-y-4">
        {/* Tipo de Centro de Costo */}
        <div className="space-y-2">
          <Label className="text-xs font-medium text-foreground sm:text-sm">
            Tipo de Centro de Costo <span className="text-xs text-red-500">(Campo obligatorio)</span>
          </Label>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <label
              className={`flex items-center p-3 transition-all border-2 rounded-lg cursor-pointer hover:bg-accent ${
                costCenter?.costCenterType === 'PROJECT' ? 'border-primary bg-primary/5' : 'border-border'
              }`}
            >
              <input
                type="radio"
                name="costCenterType"
                value="PROJECT"
                checked={costCenter?.costCenterType === 'PROJECT'}
                onChange={(e) => handleTypeChange(e.target.value as CostCenterType)}
                className="w-4 h-4 mr-3"
              />
              <Building2 className="w-5 h-5 mr-2 text-primary" />
              <span className="text-sm font-medium text-foreground">Proyecto / Obra</span>
            </label>

            <label
              className={`flex items-center p-3 transition-all border-2 rounded-lg cursor-pointer hover:bg-accent ${
                costCenter?.costCenterType === 'GENERAL' ? 'border-primary bg-primary/5' : 'border-border'
              }`}
            >
              <input
                type="radio"
                name="costCenterType"
                value="GENERAL"
                checked={costCenter?.costCenterType === 'GENERAL'}
                onChange={(e) => handleTypeChange(e.target.value as CostCenterType)}
                className="w-4 h-4 mr-3"
              />
              <Briefcase className="w-5 h-5 mr-2 text-primary" />
              <span className="text-sm font-medium text-foreground">Rubro General</span>
            </label>
          </div>
        </div>

        {/* Selector de Proyecto */}
        {costCenter?.costCenterType === 'PROJECT' && (
          <div className="space-y-2">
            <Label className="text-xs font-medium text-foreground sm:text-sm">
              Seleccionar Obra <span className="text-xs text-red-500">(Campo obligatorio)</span>
            </Label>
            <Select
              value={costCenter.projectId ? String(costCenter.projectId) : ''}
              onValueChange={(value) => handleProjectChange(Number(value))}
            >
              <SelectTrigger className="rounded-lg bg-background border-border">
                <SelectValue placeholder="Seleccionar obra..." />
              </SelectTrigger>
              <SelectContent sortAlphabetically>
                {projects.map((project) => (
                  <SelectItem key={project.id} value={String(project.id)}>
                    {project.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Rubro */}
        {((costCenter?.costCenterType === 'PROJECT' && costCenter.projectId) ||
          costCenter?.costCenterType === 'GENERAL') && (
          <div className="space-y-2">
            <Label className="text-xs font-medium text-foreground sm:text-sm">
              Rubro <span className="text-xs text-red-500">(Campo obligatorio)</span>
            </Label>
            <Select
              value={costCenter.workCategoryId ? String(costCenter.workCategoryId) : ''}
              onValueChange={(value) => handleWorkCategoryChange(Number(value))}
            >
              <SelectTrigger className="rounded-lg bg-background border-border">
                <SelectValue placeholder="Seleccionar rubro..." />
              </SelectTrigger>
              <SelectContent sortAlphabetically>
                {availableWorkCategories.map((category) => (
                  <SelectItem key={category.id} value={String(category.id)}>
                    {category.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <div className="p-3 border-l-4 border-primary rounded-r-lg bg-primary/5">
          <p className="text-sm text-foreground">
            <strong>✓</strong> Este centro de costo se aplicará automáticamente a todos los items de la orden.
          </p>
        </div>
      </div>
    </div>
  );
};
